/**
 * Dictation in the composer (R57 to R62): the microphone, the recording, the request, and the
 * state the composer draws. The composer hands over what to do with the text and nothing else.
 *
 * ── What it hides ────────────────────────────────────────────────────────────────────────────
 *   · whether dictation is offered at all, read off `GET /ai/status` (`dictationOffer`);
 *   · the permission prompt and its two refusals (blocked, no device);
 *   · the container the browser can record (`recordingType`: WebM with Opus, MP4 on Safari);
 *   · the level the waveform draws, read from an `AnalyserNode` on demand (`readLevel`), never as
 *     React state per frame;
 *   · the stop at the longest length, and the clip too short or too quiet to send (never uploaded);
 *   · the request, with the interface's language as the hint, and the recording kept for „Ponów".
 *
 * ── Never stuck ──────────────────────────────────────────────────────────────────────────────
 * The recorder's end is wired when it starts, not when the trainer stops it: a track that ends by
 * itself (unplugged, permission revoked) or a recorder error finishes the dictation with what was
 * recorded, or says the microphone is gone. A setup that throws after the grant lets the
 * microphone go. The longest length is the one offered when the recording began; an offer that goes
 * off while the browser asks or records cancels it (nothing is sent) and says why.
 *
 * ── Nothing is sent, ever ────────────────────────────────────────────────────────────────────
 * A cancel while recording stops the tracks and drops the chunks: nothing leaves the browser. A
 * cancel while transcribing cannot take the request back (it counts, R59), so its answer is
 * discarded: every request carries the generation it was made in, and an answer from an older one
 * is ignored. `onText` is the only way out, and it writes into the field.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useReducer, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { aiStatusQueryOptions, dictationOffer, transcribe, type AiDictationOffer } from '~/entities/ai-conversation';
import { resolveLanguage } from '~/shared/i18n';

import {
  DICTATION_IDLE,
  clipWorthSending,
  dictationReducer,
  levelOfFrame,
  recordingType,
  settlesByItself,
  transcriptionRefused,
  wordCount,
  type DictationState,
} from '../model/dictation';

/** How long an outcome that needs nothing from the trainer stays in the tray. */
const OUTCOME_SHOWN_MS = 6_000;

/** How often the clock, the longest-length stop and the loudest level are looked at. */
const TICK_MS = 200;

export interface Dictation {
  readonly offer: AiDictationOffer;
  readonly state: DictationState;
  /** Whole seconds recorded so far. */
  readonly elapsed: number;
  /** The longest length of the dictation under way, as offered when it began. */
  readonly maxSeconds: number;
  /** The browser refused the microphone, until it next grants it: the mic draws crossed. */
  readonly micBlocked: boolean;
  /** The level now, 0 to 1, for the waveform's frame; 0 when nothing is recording. */
  readonly readLevel: () => number;
  /** Milliseconds recorded so far, for the countdown arc's frame. */
  readonly readElapsedMs: () => number;
  readonly start: () => void;
  /** Stop and transcribe. Never sends anything to Buddy. */
  readonly stop: () => void;
  readonly cancel: () => void;
  /** Send the same recording again, after a failure that kept it. */
  readonly retry: () => void;
  /** The tray goes back to its own sentence. */
  readonly settle: () => void;
}

interface Recording {
  readonly stream: MediaStream;
  readonly recorder: MediaRecorder;
  readonly context: AudioContext;
  readonly analyser: AnalyserNode;
  readonly frame: Float32Array<ArrayBuffer>;
  readonly chunks: Blob[];
  readonly startedAt: number;
  loudest: number;
  /** Stopped at the longest length rather than by the trainer. */
  auto: boolean;
  /** Ended without being asked: its track ended, or the recorder failed. */
  lost: boolean;
}

function stopTracks(stream: MediaStream): void {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
}

function release(recording: Recording): void {
  stopTracks(recording.stream);
  void recording.context.close().catch(() => undefined);
}

/**
 * `blocked`: a usage window is full, as the composer knows it (a refusal it holds counts before
 * the status catches up). `here`: this composer offers dictation at all (R57). `holdOutcome`: the
 * trainer's focus is on the outcome's line, so it does not leave by itself. `onText` writes the
 * transcript into the field; it runs after the state has left `transcribing`, so the field is
 * writable again.
 */
export function useDictation({
  blocked,
  here,
  holdOutcome,
  onText,
}: {
  readonly blocked: boolean;
  readonly here: boolean;
  readonly holdOutcome: boolean;
  readonly onText: (text: string) => void;
}): Dictation {
  const { i18n } = useTranslation();
  const client = useQueryClient();
  const status = useQuery(aiStatusQueryOptions());
  const offer: AiDictationOffer = here ? dictationOffer(status.data, blocked) : { kind: 'none' };
  const [state, dispatch] = useReducer(dictationReducer, DICTATION_IDLE);
  const [elapsed, setElapsed] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(0);
  const [micBlocked, setMicBlocked] = useState(false);
  const recording = useRef<Recording | null>(null);
  const kept = useRef<Blob | null>(null);
  const generation = useRef(0);
  const ticker = useRef<number | undefined>(undefined);
  // The recorder's end and the request's answer arrive long after the render that started them;
  // they write through the composer's newest `onText`, which knows where the caret stood.
  const writeText = useRef(onText);
  useEffect(() => {
    writeText.current = onText;
  });

  useEffect(
    () => () => {
      generation.current += 1;
      window.clearInterval(ticker.current);
      if (recording.current !== null) release(recording.current);
      recording.current = null;
    },
    [],
  );

  // An outcome that asks nothing of the trainer gives the tray back after a moment — but not from
  // under the trainer's focus. One that offers an act stays until the trainer types or acts.
  const quiet = state.phase === 'idle' && settlesByItself(state.outcome) && !holdOutcome;
  useEffect(() => {
    if (!quiet) return;
    const timer = window.setTimeout(() => {
      dispatch({ type: 'settle' });
    }, OUTCOME_SHOWN_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [quiet, state.outcome]);

  const send = (clip: Blob) => {
    const mine = generation.current;
    void transcribe(clip, resolveLanguage(i18n.language)).then((answer) => {
      if (mine !== generation.current) return;
      if (answer.outcome === 'heard') {
        kept.current = null;
        if (answer.text === '') {
          dispatch({ type: 'silent' });
          return;
        }
        flushSync(() => {
          dispatch({ type: 'heard', words: wordCount(answer.text) });
        });
        writeText.current(answer.text);
        return;
      }
      const { refusal } = answer;
      // A full window or a switched-off Buddy is the status's to say: the microphone follows it.
      if (refusal.kind === 'limit' || refusal.kind === 'unavailable') void client.invalidateQueries({ queryKey: aiStatusQueryOptions().queryKey });
      const event = transcriptionRefused(refusal);
      if (event.type !== 'failed' || !event.retryable) kept.current = null;
      dispatch(event);
    });
  };

  // The recording's one ending, however it came: the trainer's stop, the longest length, the track
  // ending by itself, or the recorder failing.
  const conclude = (current: Recording, mine: number) => {
    release(current);
    if (mine !== generation.current) return;
    window.clearInterval(ticker.current);
    if (recording.current === current) recording.current = null;
    const heard = current.chunks.length > 0 && clipWorthSending(performance.now() - current.startedAt, current.loudest);
    if (current.lost && !heard) {
      dispatch({ type: 'lost' });
      return;
    }
    dispatch({ type: 'stop', auto: current.auto, heard });
    if (!heard) return;
    const clip = new Blob(current.chunks, { type: current.recorder.mimeType || current.chunks[0]?.type || 'audio/webm' });
    kept.current = clip;
    send(clip);
  };

  const finish = (auto: boolean) => {
    const current = recording.current;
    if (current === null || current.recorder.state === 'inactive') return;
    window.clearInterval(ticker.current);
    current.auto = auto;
    current.recorder.stop();
  };

  const sample = (current: Recording): number => {
    current.analyser.getFloatTimeDomainData(current.frame);
    const level = levelOfFrame(current.frame);
    if (level > current.loudest) current.loudest = level;
    return level;
  };

  const begin = (stream: MediaStream, mine: number): Recording => {
    const type = recordingType((candidate) => MediaRecorder.isTypeSupported(candidate));
    const recorder = new MediaRecorder(stream, type === null ? undefined : { mimeType: type });
    const context = new AudioContext();
    try {
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      const current: Recording = {
        stream,
        recorder,
        context,
        analyser,
        frame: new Float32Array(analyser.fftSize),
        chunks: [],
        startedAt: performance.now(),
        loudest: 0,
        auto: false,
        lost: false,
      };
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) current.chunks.push(event.data);
      };
      recorder.onstop = () => {
        conclude(current, mine);
      };
      recorder.onerror = () => {
        current.lost = true;
        if (recorder.state !== 'inactive') recorder.stop();
      };
      stream.getTracks().forEach((track) => {
        track.onended = () => {
          current.lost = true;
          if (recorder.state !== 'inactive') recorder.stop();
        };
      });
      recorder.start(1_000);
      return current;
    } catch (error) {
      void context.close().catch(() => undefined);
      throw error;
    }
  };

  const start = () => {
    if (offer.kind !== 'live' || state.phase !== 'idle') return;
    generation.current += 1;
    const mine = generation.current;
    kept.current = null;
    setElapsed(0);
    setMaxSeconds(offer.maxSeconds);
    dispatch({ type: 'start' });
    const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (devices === undefined || typeof MediaRecorder === 'undefined') {
      dispatch({ type: 'micRefused', why: 'noDevice' });
      return;
    }
    devices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }).then(
      (stream) => {
        if (mine !== generation.current) {
          stopTracks(stream);
          return;
        }
        let current: Recording;
        try {
          current = begin(stream, mine);
        } catch {
          // The browser granted a microphone it cannot record from: let it go, never stay asking.
          stopTracks(stream);
          dispatch({ type: 'micRefused', why: 'noDevice' });
          return;
        }
        recording.current = current;
        setMicBlocked(false);
        dispatch({ type: 'granted' });
      },
      (error: unknown) => {
        if (mine !== generation.current) return;
        const name = error instanceof DOMException ? error.name : '';
        const denied = name === 'NotAllowedError' || name === 'SecurityError';
        if (denied) setMicBlocked(true);
        dispatch({ type: 'micRefused', why: denied ? 'denied' : 'noDevice' });
      },
    );
  };

  // Let the microphone go and drop what was recorded: nothing leaves the browser.
  const abandon = () => {
    generation.current += 1;
    window.clearInterval(ticker.current);
    const current = recording.current;
    recording.current = null;
    kept.current = null;
    if (current === null) return;
    current.recorder.onstop = null;
    current.recorder.ondataavailable = null;
    if (current.recorder.state !== 'inactive') current.recorder.stop();
    release(current);
  };

  // The offer went off while the browser asked or recorded (the window filled, Buddy was switched
  // off, the field became a rejection's reason): cancel, send nothing, and say why.
  const offerGone = useEffectEvent(() => {
    abandon();
    dispatch(offer.kind === 'off' ? { type: 'refused', reason: offer.reason } : { type: 'cancel' });
  });
  const capturing = state.phase === 'requesting' || state.phase === 'recording';
  const live = offer.kind === 'live';
  useEffect(() => {
    if (capturing && !live) offerGone();
  }, [capturing, live]);

  // The clock, the stop at the longest length, and the loudest level (the waveform may not be
  // drawing: reduced motion draws a still shape).
  const tick = useEffectEvent(() => {
    const current = recording.current;
    if (current === null) return;
    sample(current);
    const seconds = (performance.now() - current.startedAt) / 1_000;
    setElapsed(Math.min(Math.floor(seconds), maxSeconds));
    if (seconds >= maxSeconds) finish(true);
  });
  const recordingNow = state.phase === 'recording';
  useEffect(() => {
    if (!recordingNow) return;
    ticker.current = window.setInterval(tick, TICK_MS);
    return () => {
      window.clearInterval(ticker.current);
    };
  }, [recordingNow]);

  return {
    offer,
    state,
    elapsed,
    maxSeconds,
    micBlocked,
    readLevel: () => (recording.current === null ? 0 : sample(recording.current)),
    readElapsedMs: () => (recording.current === null ? 0 : performance.now() - recording.current.startedAt),
    start,
    stop: () => {
      finish(false);
    },
    cancel: () => {
      if (state.phase === 'idle') return;
      abandon();
      dispatch({ type: 'cancel' });
    },
    retry: () => {
      const clip = kept.current;
      if (clip === null || state.phase !== 'idle' || state.outcome?.kind !== 'failed') return;
      generation.current += 1;
      dispatch({ type: 'retry' });
      send(clip);
    },
    settle: () => {
      // The outcome's line leaves, and with it „Ponów": the recording kept for it goes too.
      kept.current = null;
      dispatch({ type: 'settle' });
    },
  };
}
